import { CalendarClock, ShoppingBasket } from "lucide-react";
import { List, SampleListItem } from "modules/lists/types/lists";


import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface ListCardProps extends React.HTMLAttributes<HTMLDivElement> {
    list: List;
    sampleProducts: SampleListItem[];
    isSelected: boolean;
    onSelect: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

/**
 * Displays a single list as a selectable card with overflow actions.
 * Shows the list icon, name, item count badge and relative creation date.
 */
const ListCard: React.FC<ListCardProps & React.HTMLAttributes<HTMLDivElement>> = ({ list, sampleProducts, isSelected, onSelect, onEdit, onDelete, ...props }) => {
    const relativeDate = formatDistanceToNow(new Date(list.updatedAt), {
        addSuffix: true,
        locale: es,
    });

    // Take up to 3 items that have an image; pad with nulls if fewer are available
    const previewImgs = [
        ...sampleProducts.filter((p) => p.itemImgUrl).slice(0, 3).map((p) => p.itemImgUrl as string),
    ];
    // Positions for 1, 2, or 3 images — always centered
    const positionsByCount: Record<number, string[]> = {
        1: ["product-center"],
        2: ["product-left-2", "product-right-2"],
        3: ["product-left", "product-center", "product-right"],
    };  
    const positionSet = positionsByCount[previewImgs.length] ?? [];

    return (
        <Card className={cn("pb-0 gap-3 pt-2 px-0 cursor-pointer",props.className)} onClick={onSelect}>
            <CardHeader className="w-full flex justify-between px-2">
                <span className="text-xs text-muted-foreground">
                    <CalendarClock className="w-4 h-4 inline-block mr-1"/> {relativeDate}
                </span>
                <span className="text-xs text-muted-foreground">
                    <ShoppingBasket className="w-4 h-4 inline-block mr-1"/> {list.itemCount}
                </span>                
            </CardHeader>
            <CardContent className="flex flex-col justify-between gap-2 h-full">
                <div className="w-full">
                    <p className="text-center font-bold text-lg">{list.name}</p>
                    <p className="text-center text-xs text-muted-foreground">{list.description}</p>
                </div>
                <div className="flex w-full relative justify-center overflow-hidden gap-0 py-4 h-24">
                    {previewImgs.map((src, i) => (
                        <img
                            key={i}
                            className={cn("image-list-card mix-blend-multiply absolute", positionSet[i])}
                            src={src}
                            alt={list.name}
                        />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};


export { ListCard };
